import { getIn } from "formik";

/**
 * Reporting a hanging protocol document's validation on the fields it is about.
 *
 * Formik indexes `errors` and `touched` by the same path it indexes `values` by,
 * so a message for `panelsLayout.panels[0].layout.viewports[3].rotation` is what
 * puts the error under that viewport's Rotation box rather than in a bar at the
 * foot of the step. Both V2 wizards hand their document to Formik for exactly
 * that, and the editor sections take the result as a `field` prop — which is why
 * this lives in helpers rather than beside the wizards: the components under
 * components/HangingProtocolV2 must not reach up into pages to find it.
 */

/**
 * The `{ errorMessage, onBlur }` pair a Fluent field needs to carry its own
 * error, keyed by the field's path in the document.
 *
 * A nested field is addressed by writing its path out —
 * `field("panelsLayout.panels[0].layout.viewports[3].rotation")` — and the
 * layout step anchors the inspector's relative names to whichever viewport is
 * selected, so the inspector stays ignorant of its own coordinates.
 *
 * Nothing is said until the author has been in the box and left it, or has
 * pressed Save, which marks every failing field touched. A blank form does not
 * open covered in what it has not been given yet.
 */
export const createFieldProps = (formik) => (path) => {
  const error = getIn(formik.errors, path);
  return {
    // An error on a parent — the array itself, say — is an object, not a message.
    errorMessage: getIn(formik.touched, path) && typeof error === "string" ? error : undefined,
    onBlur: () => formik.setFieldTouched(path, true),
  };
};

/**
 * The stand-in for anything rendering an editor outside a form. Every section
 * takes `field` optionally, so read-only mode, the composed
 * `ProtocolEditor`/`GroupEditor` exports and the tests need know nothing about it.
 */
export const NO_FIELD_PROPS = () => ({});

/**
 * The first viewport carrying an error, as the coordinates the layout step
 * selects by.
 *
 * The inspector shows one viewport at a time, so without this a bad value in an
 * unselected cell would block Save with its message on a panel nobody is looking
 * at. Formik builds `errors` with the same shape as the document, so this walks
 * it the same way the document is walked.
 */
export const firstInvalidViewport = (errors) => {
  const panels = getIn(errors, "panelsLayout.panels") ?? [];
  for (let panelIndex = 0; panelIndex < panels.length; panelIndex += 1) {
    const viewports = panels[panelIndex]?.layout?.viewports ?? [];
    for (let viewportIndex = 0; viewportIndex < viewports.length; viewportIndex += 1) {
      if (viewports[viewportIndex]) return { panelIndex, viewportIndex };
    }
  }
  return null;
};
